// @tg-seq: 10874

// @architect-approved: HARDCODED_COLORS - Availability status legend uses fixed semantic colors (grey/green/yellow/red), not theme-dependent
// @architect-approved: independent panels - /api/user-presence (bulk roster presence display),
// /api/entra-presence-optin (self opt-in settings toggle), and /api/entra-presence-sync (manual
// Graph sync trigger action) are three genuinely independent concerns, not assembled into one
// merged view/state object. Each is its own user-initiated action/display, not a client-side join.
// @tg-mockup: High-fidelity UI mockup — local mock data only, no backend wiring yet.
// @tg-mockup: Organization filter, Dept/DeptGroup/Org-Hierarchy sort, Co-Tenancy
// toggle, and Search are all client-side over mock data for UX validation only.
// Backend wiring (dynamic WHERE clauses for Team/Customers/Suppliers, shared
// search algorithm) is a follow-up task per architect note 2026-09-10.

// === AUTO-GENERATED START: API_ENDPOINTS ===
/**
 * API Endpoint Registry - Component 10874
 * Generated from Router_Registry (TSX_Seq_Num = 10874)
 * Auto-synced on build - DO NOT EDIT MANUALLY
 *
 * Router 1: tsx_10874_1.py (TSX_COMPONENT)
 *   Controller: TEL.sp_Voice_Message_Controller
 *   Endpoint: /api/tsx/10874/1
 *   Usage: Voicemail inbox CRUD for Speed Dial (TEL.Voice_Message) - send/list/mark-listened/delete
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const API_ENDPOINTS = {
    MAIN: '/api/tsx/10874/1',
};
// === AUTO-GENERATED END: API_ENDPOINTS ===

import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Avatar,
    Badge,
    IconButton,
    Button,
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Chat as ChatIcon,
    VoicemailRounded as VoicemailIcon,
    Email as EmailIcon,
    VideoCall as TeamsCallIcon,
    Event as MeetingIcon,
    AccessTime as TimeclockIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import { apiRequest, apiRequestBySeq } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { useChat } from '../../utils/useChat';
import { useAuth } from '../../utils/useAuth';

// Synthesizes a demo email address from a display name (mockup only — no real
// directory lookup yet). Used to build Teams deep links for the prototype.
const emailFor = (name: string): string =>
    `${name.trim().toLowerCase().replace(/\s+/g, '.')}@company.com`;

// Fires the Teams desktop protocol handler for a given web deep link, and only
// falls back to opening the web client in a new tab if the desktop app didn't
// appear to take over. Browsers give JS no direct success/failure callback for
// a custom URI scheme (msteams://), so this uses the Page Visibility API as a
// proxy signal: if the OS actually switches focus to the Teams desktop app,
// this tab fires 'visibilitychange' (hidden) or 'blur' within ~1s. If neither
// fires, the desktop app likely isn't installed/registered, so we show the web
// client instead. This avoids the previous behavior of always opening a
// redundant browser tab even when the desktop handoff succeeded.
const launchTeamsDeepLink = (webUrl: string): void => {
    let appLikelyOpened = false;
    const markOpened = () => {
        if (document.hidden) {
            appLikelyOpened = true;
        }
    };
    const onBlur = () => {
        appLikelyOpened = true;
    };
    document.addEventListener('visibilitychange', markOpened);
    window.addEventListener('blur', onBlur);

    window.location.href = `msteams://${webUrl.replace('https://', '')}`;

    setTimeout(() => {
        document.removeEventListener('visibilitychange', markOpened);
        window.removeEventListener('blur', onBlur);
        if (!appLikelyOpened) {
            window.open(webUrl, '_blank');
        }
    }, 1000);
};

// Launches a 1:1 Teams call via the desktop app protocol handler, falling back
// to the web client if Teams isn't installed. Same pattern as the working
// Teams join button in scr_tgc_10994_meeting_master.tsx. Prefers the contact's
// real preferred email (ORG.org_pfuser_association_master.Preferred_Email)
// when available, same as the Email icon — only falls back to the synthesized
// name@company.com mock for contacts with no real email on file yet.
const launchTeamsCall = (name: string, realEmail?: string | null): void => {
    const email = realEmail || emailFor(name);
    launchTeamsDeepLink(
        `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(email)}`,
    );
};

// Launches Teams' "Schedule a meeting" compose view with this contact
// pre-filled as an attendee — same desktop-app-first, web-fallback pattern as
// launchTeamsCall above, and same real-email-first preference.
const launchTeamsMeeting = (name: string, realEmail?: string | null): void => {
    const email = realEmail || emailFor(name);
    launchTeamsDeepLink(
        `https://teams.microsoft.com/l/meeting/new?attendees=${encodeURIComponent(email)}`,
    );
};

// Availability status: grey=not online, green=available, yellow=on call (not scheduled), red=busy in meeting
type AvailabilityStatus = 'offline' | 'available' | 'oncall' | 'busy';

const getStatusColor = (status: AvailabilityStatus): string => {
    switch (status) {
        case 'available':
            return '#4caf50'; // green
        case 'oncall':
            return '#ffc107'; // yellow
        case 'busy':
            return '#f44336'; // red
        case 'offline':
        default:
            return '#9e9e9e'; // grey
    }
};

// CollabCore = internal platform user who has opted into VOIP/chat/video
// availability. External = reachable only via outside phone number / email
// (not yet opted in, or belongs to another organization without co-tenancy).
type ChannelType = 'collabcore' | 'external';

interface TeamContact {
    pfuser_uuid: string;
    name: string;
    department: string;
    deptGroup: string;
    orgHierarchy: string;
    organization: string;
    status: AvailabilityStatus;
    channel: ChannelType;
    // true only when OrgVOIP_Optin_Status_Id === 1 (Active) - gates the Teams
    // call + voicemail icons independent of the channel badge/proxy.
    voipOptedIn: boolean;
    // ORG.org_pfuser_association_master.external_phone - the outside number on
    // file for this association. Real dial target for External contacts'
    // phone icon (CollabCore contacts don't need this yet - their Phone icon
    // still falls back to the bare tel: launch pending internal-extension work).
    externalPhone: string | null;
    // ORG.org_pfuser_association_master.Preferred_Email - real mailto target
    // for External contacts' Email icon (falls back to the synthesized
    // name@company.com mock when not set, same as before this was wired).
    externalEmail: string | null;
}

// Raw shape returned by ORG.sp_Universal_Contact_Search_Controller (@Manage=5)
// via apiRequestBySeq(11201, 1, ...) - the same fan-out org/hierarchy/dept/
// deptgroup search already used by Universal Contact Search
// (scr_tgc_11201_universal_contact_search.tsx), reused here as the My Team
// data source instead of standing up a duplicate endpoint.
interface ContactSearchRow {
    pfuser_uuid: string;
    organization_uuid: string;
    organization_name: string;
    display_name: string;
    full_name: string;
    orgmgthierarchy: string | null;
    role_department: string | null;
    deptgroup: string | null;
    // Whether this pfuser has opted into peers seeing their presence
    // (ADM.Profile_PFUser_Communications.Show_Online_Status). INTERIM phase-1
    // proxy for the CollabCore/External channel split - the fuller hybrid
    // model (separate opt-in flags for internal calls/chat/video vs. a
    // left-joined personal phone/email fallback) is a follow-up phase per
    // architect note 2026-10-03.
    show_online_status: boolean;
    // ORG.org_pfuser_association_master.OrgVOIP_Optin_Status_Id (1=Active,
    // 2=User Opted Out, 3=Organization Blocking, 0=Undefined) - now joined
    // into ORG.vw_Universal_Contact_Search_Full. Drives real VOIP-modality
    // gating (Teams call + voicemail) per architect note 2026-10-05, ahead
    // of channel/badge itself moving off the show_online_status proxy.
    orgvoip_optin_status_id: number | null;
    // ORG.org_pfuser_association_master.external_phone, now joined into
    // ORG.vw_Universal_Contact_Search_Full as External_Phone.
    external_phone: string | null;
    // ORG.org_pfuser_association_master.Preferred_Email, now joined into
    // ORG.vw_Universal_Contact_Search_Full.
    preferred_email: string | null;
}

// TEL.UserPresence.Presence_State values -> Speed Dial's AvailabilityStatus.
// DoNotDisturb collapses onto 'busy' (closest existing semantic/color) since
// the UI currently only distinguishes 4 states.
const mapPresenceState = (state: string | null | undefined): AvailabilityStatus => {
    switch (state) {
        case 'Available':
            return 'available';
        case 'OnCall':
            return 'oncall';
        case 'Busy':
        case 'DoNotDisturb':
            return 'busy';
        case 'Offline':
        default:
            return 'offline';
    }
};

interface OrgContact {
    orgLegalName: string;
    name: string;
    channel: ChannelType;
    // true = we share tenancy with this org and can see their published
    // contact numbers/availability; false = outside contact info only.
    coTenant: boolean;
}

interface PersonalContact {
    name: string;
    relation?: string;
    channel: ChannelType;
}

// My Team roster is now live (see fetchTeamContacts in Self below) - fetched
// from ORG.sp_Universal_Contact_Search_Controller + TEL.sp_UserPresence_Controller.
// Customers/Suppliers below remain mock pending the org-to-org relationship
// phase (myorg_theirorg_association_controller.py / mod_tgc_10832).

const customerContacts: OrgContact[] = [
    {
        orgLegalName: 'Acme Industrial Supply LLC',
        name: 'Robert Martinez',
        channel: 'collabcore',
        coTenant: true,
    },
    {
        orgLegalName: 'Blue Ridge Manufacturing Inc.',
        name: 'Jennifer Davis',
        channel: 'external',
        coTenant: false,
    },
    {
        orgLegalName: 'Northgate Retail Group',
        name: 'Amanda Foster',
        channel: 'collabcore',
        coTenant: true,
    },
    {
        orgLegalName: 'Summit Logistics Corp.',
        name: 'Carlos Nguyen',
        channel: 'external',
        coTenant: false,
    },
];

const supplierContacts: OrgContact[] = [
    {
        orgLegalName: 'Titan Steel & Fabrication',
        name: 'Patricia Owens',
        channel: 'collabcore',
        coTenant: true,
    },
    {
        orgLegalName: 'Coastal Freight Partners',
        name: 'Brian Kessler',
        channel: 'external',
        coTenant: false,
    },
    {
        orgLegalName: 'Pinnacle Packaging Co.',
        name: 'Diana Alvarez',
        channel: 'collabcore',
        coTenant: true,
    },
];

const personalNetworkContacts: PersonalContact[] = [
    { name: 'Tom Bradley', relation: 'Mentor', channel: 'external' },
    { name: 'Rachel Kim', relation: 'Former Colleague', channel: 'external' },
    { name: 'Steve Ackerman', relation: 'Industry Contact', channel: 'external' },
];

const familyContacts: PersonalContact[] = [
    { name: 'Maria Gonzalez', relation: 'Spouse', channel: 'external' },
    { name: 'Daniel Gonzalez', relation: 'Son', channel: 'external' },
    { name: 'Sofia Gonzalez', relation: 'Daughter', channel: 'external' },
];

const initials = (name: string): string =>
    name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();

// Reusable row of speed-dial action icons (phone / chat / Teams call / voice message / email).
// CollabCore contacts get the full internal-platform action set; External
// contacts (not opted into CollabCore VOIP) only get a real phone number + email.
interface ContactActionHandlers {
    onCall: (label: string, phoneNumber?: string) => void;
    onChat: (label: string) => void;
    onTeamsCall: (label: string, email?: string) => void;
    onVoicemail: (label: string, pfuserUuid?: string) => void;
    onMeeting: (label: string, email?: string) => void;
    onTimeclock: (label: string, pfuserUuid?: string) => void;
}

const ContactActions: React.FC<
    {
        label: string;
        channel: ChannelType;
        voipOptedIn?: boolean;
        phoneNumber?: string | null;
        email?: string | null;
        pfuserUuid?: string;
    } & ContactActionHandlers
> = ({
    label,
    channel,
    voipOptedIn,
    phoneNumber,
    email,
    pfuserUuid,
    onCall,
    onChat,
    onTeamsCall,
    onVoicemail,
    onMeeting,
    onTimeclock,
}) => {
    const isCollabCore = channel === 'collabcore';
    // Teams call + voicemail are gated on real VOIP opt-in when the caller
    // supplies it (My Team, backed by OrgVOIP_Optin_Status_Id). Mock lists
    // (Customers/Suppliers/Personal/Family) don't pass voipOptedIn yet, so
    // they fall back to the channel proxy to preserve existing behavior.
    const voiceEnabled = voipOptedIn ?? isCollabCore;
    // No per-row channel chip here anymore — the contact is already grouped under
    // a CollabCore/External section header (see ChannelSectionHeader below), which
    // is what actually communicates the badge meaning. Icon slots are fixed per
    // channel (always Teams Call + Voicemail slots for CollabCore, just disabled
    // when this specific contact isn't VOIP opted-in) so every row within a
    // section lines up into a real aligned grid instead of shifting per-row.
    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Tooltip
                title={
                    isCollabCore
                        ? `Call ${label} via CollabCore`
                        : `Call ${label} (external number)`
                }
            >
                <IconButton
                    size="small"
                    color={isCollabCore ? 'primary' : 'default'}
                    onClick={() => onCall(label, phoneNumber ?? undefined)}
                >
                    <PhoneIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {isCollabCore && (
                <>
                    <Tooltip title={`Chat ${label}`}>
                        <IconButton size="small" color="primary" onClick={() => onChat(label)}>
                            <ChatIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            voiceEnabled
                                ? `Teams call ${label}`
                                : `${label} hasn't opted into Teams Voice`
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={!voiceEnabled}
                                onClick={() => onTeamsCall(label, email ?? undefined)}
                            >
                                <TeamsCallIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            voiceEnabled
                                ? `Leave voice message for ${label}`
                                : `${label} hasn't opted into Teams Voice`
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={!voiceEnabled}
                                onClick={() => onVoicemail(label, pfuserUuid)}
                            >
                                <VoicemailIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={`Schedule a meeting with ${label}`}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onMeeting(label, email ?? undefined)}
                        >
                            <MeetingIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`View ${label}'s timeclock`}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onTimeclock(label, pfuserUuid)}
                        >
                            <TimeclockIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            )}
            <Tooltip title={`Email ${label}`}>
                <IconButton
                    size="small"
                    color={isCollabCore ? 'primary' : 'default'}
                    component="a"
                    href={`mailto:${email || emailFor(label)}`}
                >
                    <EmailIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
};

// Dual-column action row for "My Team" (common associated pfusers) only.
// Column 1 = internal/CollabCore modes (Chat, Teams Call, Voicemail) — blue +
// enabled only when voipOptedIn is true, grey + disabled (never hidden) when
// it isn't, so every row lines up into the same grid regardless of opt-in
// status. Column 2 = external/direct modes (Phone, Email) — always shown,
// using the real external_phone/preferred_email on file (falling back to the
// synthesized mock when not set). The two columns are independent: a contact
// can be opted into CollabCore AND still have an external number on file.
const TeamContactActions: React.FC<
    {
        label: string;
        pfuserUuid: string;
        voipOptedIn: boolean;
        phoneNumber?: string | null;
        email?: string | null;
    } & ContactActionHandlers
> = ({
    label,
    pfuserUuid,
    voipOptedIn,
    phoneNumber,
    email,
    onCall,
    onChat,
    onTeamsCall,
    onVoicemail,
    onMeeting,
    onTimeclock,
}) => (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Tooltip
                title={voipOptedIn ? `Chat ${label}` : `${label} hasn't opted into CollabCore`}
            >
                <span>
                    <IconButton
                        size="small"
                        color={voipOptedIn ? 'primary' : 'default'}
                        disabled={!voipOptedIn}
                        onClick={() => onChat(label)}
                    >
                        <ChatIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    voipOptedIn ? `Teams call ${label}` : `${label} hasn't opted into CollabCore`
                }
            >
                <span>
                    <IconButton
                        size="small"
                        color={voipOptedIn ? 'primary' : 'default'}
                        disabled={!voipOptedIn}
                        onClick={() => onTeamsCall(label, email ?? undefined)}
                    >
                        <TeamsCallIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    voipOptedIn
                        ? `Leave voice message for ${label}`
                        : `${label} hasn't opted into CollabCore`
                }
            >
                <span>
                    <IconButton
                        size="small"
                        color={voipOptedIn ? 'primary' : 'default'}
                        disabled={!voipOptedIn}
                        onClick={() => onVoicemail(label, pfuserUuid)}
                    >
                        <VoicemailIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    voipOptedIn
                        ? `Schedule a meeting with ${label}`
                        : `${label} hasn't opted into CollabCore`
                }
            >
                <span>
                    <IconButton
                        size="small"
                        color={voipOptedIn ? 'primary' : 'default'}
                        disabled={!voipOptedIn}
                        onClick={() => onMeeting(label, email ?? undefined)}
                    >
                        <MeetingIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    voipOptedIn
                        ? `View ${label}'s timeclock`
                        : `${label} hasn't opted into CollabCore`
                }
            >
                <span>
                    <IconButton
                        size="small"
                        color={voipOptedIn ? 'primary' : 'default'}
                        disabled={!voipOptedIn}
                        onClick={() => onTimeclock(label, pfuserUuid)}
                    >
                        <TimeclockIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Tooltip title={`Call ${label}${phoneNumber ? '' : ' (no external number on file)'}`}>
                <IconButton size="small" onClick={() => onCall(label, phoneNumber ?? undefined)}>
                    <PhoneIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={`Email ${label}`}>
                <IconButton size="small" component="a" href={`mailto:${email || emailFor(label)}`}>
                    <EmailIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    </Stack>
);

// Replaces the old per-row CollabCore/External chip — contacts are now grouped
// into two sections (CollabCore first, External second) so each section forms a
// real aligned grid (every row in a section has the same icon-column layout),
// instead of a flat list where the badge appeared to float between rows of
// differing width.
// NOTE: still used by OrgList/PersonalList (Customers/Suppliers/Personal/Family
// tabs) — My Team no longer uses this, see TeamContactActions above.
const ChannelSectionHeader: React.FC<{ channel: ChannelType; count: number }> = ({
    channel,
    count,
}) => {
    const isCollabCore = channel === 'collabcore';
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Chip
                size="small"
                label={isCollabCore ? 'CollabCore' : 'External'}
                color={isCollabCore ? 'primary' : 'default'}
                variant={isCollabCore ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
            <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 0.5, lineHeight: 1 }}
            >
                {isCollabCore ? 'Internal Contacts' : 'External Contacts'} ({count})
            </Typography>
        </Box>
    );
};

// Legend explaining the CollabCore / External chip shown on every contact row —
// answers "why do some rows show more icons than others".
const ContactChannelLegend: React.FC = () => (
    <Stack spacing={0.5} sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
        <Stack
            direction="row"
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <Chip
                size="small"
                label="CollabCore Contact"
                color="primary"
                variant="filled"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                }}
            >
                VOIP call, chat, Teams video &amp; voicemail via internal platform
            </Typography>
        </Stack>
        <Stack
            direction="row"
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <Chip
                size="small"
                label="External Contact"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                }}
            >
                Outside phone number &amp; email only — not yet opted into CollabCore
            </Typography>
        </Stack>
    </Stack>
);

const renderTeamContactRow = (
    contact: TeamContact,
    isLast: boolean,
    { onCall, onChat, onTeamsCall, onVoicemail, onMeeting, onTimeclock }: ContactActionHandlers,
) => (
    <React.Fragment key={contact.pfuser_uuid}>
        <ListItem
            secondaryAction={
                <TeamContactActions
                    label={contact.name}
                    pfuserUuid={contact.pfuser_uuid}
                    voipOptedIn={contact.voipOptedIn}
                    phoneNumber={contact.externalPhone}
                    email={contact.externalEmail}
                    onCall={onCall}
                    onChat={onChat}
                    onTeamsCall={onTeamsCall}
                    onVoicemail={onVoicemail}
                    onMeeting={onMeeting}
                    onTimeclock={onTimeclock}
                />
            }
            sx={{ py: 1 }}
        >
            <ListItemAvatar>
                <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: getStatusColor(contact.status),
                                border: '2px solid',
                                borderColor: 'background.paper',
                            }}
                        />
                    }
                >
                    <Avatar>{initials(contact.name)}</Avatar>
                </Badge>
            </ListItemAvatar>
            <ListItemText
                primary={contact.name}
                secondary={`${contact.department} • ${contact.organization}`}
            />
        </ListItem>
        {!isLast && <Divider component="li" />}
    </React.Fragment>
);

// Single flat list — every contact gets both an internal/CollabCore actions
// column and an external actions column per row (see TeamContactActions),
// instead of being segregated into separate CollabCore/External sections.
const TeamList: React.FC<{ contacts: TeamContact[] } & ContactActionHandlers> = ({
    contacts,
    onCall,
    onChat,
    onTeamsCall,
    onVoicemail,
    onMeeting,
    onTimeclock,
}) => {
    const handlers: ContactActionHandlers = {
        onCall,
        onChat,
        onTeamsCall,
        onVoicemail,
        onMeeting,
        onTimeclock,
    };

    if (contacts.length === 0) {
        return (
            <List disablePadding>
                <ListItem>
                    <ListItemText primary="No team members match the current filters." />
                </ListItem>
            </List>
        );
    }

    return (
        <List disablePadding>
            {contacts.map((c, idx) =>
                renderTeamContactRow(c, idx === contacts.length - 1, handlers),
            )}
        </List>
    );
};

const renderOrgContactRow = (
    contact: OrgContact,
    isLast: boolean,
    { onCall, onChat, onTeamsCall, onVoicemail, onMeeting, onTimeclock }: ContactActionHandlers,
) => (
    <React.Fragment key={contact.orgLegalName + contact.name}>
        <ListItem
            secondaryAction={
                <ContactActions
                    label={contact.name}
                    channel={contact.channel}
                    onCall={onCall}
                    onChat={onChat}
                    onTeamsCall={onTeamsCall}
                    onVoicemail={onVoicemail}
                    onMeeting={onMeeting}
                    onTimeclock={onTimeclock}
                />
            }
            sx={{ py: 1 }}
        >
            <ListItemAvatar>
                <Avatar>{initials(contact.name)}</Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={contact.name}
                secondary={
                    <>
                        {contact.orgLegalName}
                        {contact.coTenant && (
                            <Chip
                                size="small"
                                label="Shared Tenancy"
                                variant="outlined"
                                color="success"
                                sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                            />
                        )}
                    </>
                }
            />
        </ListItem>
        {!isLast && <Divider component="li" />}
    </React.Fragment>
);

// Grouped into CollabCore / External sections — same pattern as TeamList.
const OrgList: React.FC<{ contacts: OrgContact[] } & ContactActionHandlers> = ({
    contacts,
    onCall,
    onChat,
    onTeamsCall,
    onVoicemail,
    onMeeting,
    onTimeclock,
}) => {
    const handlers: ContactActionHandlers = {
        onCall,
        onChat,
        onTeamsCall,
        onVoicemail,
        onMeeting,
        onTimeclock,
    };
    const collabcore = contacts.filter((c) => c.channel === 'collabcore');
    const external = contacts.filter((c) => c.channel === 'external');

    if (contacts.length === 0) {
        return (
            <List disablePadding>
                <ListItem>
                    <ListItemText primary="No contacts match the current filters." />
                </ListItem>
            </List>
        );
    }

    return (
        <>
            {collabcore.length > 0 && (
                <>
                    <ChannelSectionHeader channel="collabcore" count={collabcore.length} />
                    <List disablePadding>
                        {collabcore.map((c, idx) =>
                            renderOrgContactRow(c, idx === collabcore.length - 1, handlers),
                        )}
                    </List>
                </>
            )}
            {external.length > 0 && (
                <>
                    <ChannelSectionHeader channel="external" count={external.length} />
                    <List disablePadding>
                        {external.map((c, idx) =>
                            renderOrgContactRow(c, idx === external.length - 1, handlers),
                        )}
                    </List>
                </>
            )}
        </>
    );
};

const renderPersonalContactRow = (
    contact: PersonalContact,
    isLast: boolean,
    { onCall, onChat, onTeamsCall, onVoicemail, onMeeting, onTimeclock }: ContactActionHandlers,
) => (
    <React.Fragment key={contact.name}>
        <ListItem
            secondaryAction={
                <ContactActions
                    label={contact.name}
                    channel={contact.channel}
                    onCall={onCall}
                    onChat={onChat}
                    onTeamsCall={onTeamsCall}
                    onVoicemail={onVoicemail}
                    onMeeting={onMeeting}
                    onTimeclock={onTimeclock}
                />
            }
            sx={{ py: 1 }}
        >
            <ListItemAvatar>
                <Avatar>{initials(contact.name)}</Avatar>
            </ListItemAvatar>
            <ListItemText primary={contact.name} secondary={contact.relation} />
        </ListItem>
        {!isLast && <Divider component="li" />}
    </React.Fragment>
);

// Grouped into CollabCore / External sections — same pattern as TeamList. Personal
// Network / Family are external-only in today's mock data, so only the External
// section renders in practice, but this stays consistent if CollabCore personal
// contacts are ever added.
const PersonalList: React.FC<{ contacts: PersonalContact[] } & ContactActionHandlers> = ({
    contacts,
    onCall,
    onChat,
    onTeamsCall,
    onVoicemail,
    onMeeting,
    onTimeclock,
}) => {
    const handlers: ContactActionHandlers = {
        onCall,
        onChat,
        onTeamsCall,
        onVoicemail,
        onMeeting,
        onTimeclock,
    };
    const collabcore = contacts.filter((c) => c.channel === 'collabcore');
    const external = contacts.filter((c) => c.channel === 'external');

    if (contacts.length === 0) {
        return (
            <List disablePadding>
                <ListItem>
                    <ListItemText primary="No contacts match the current search." />
                </ListItem>
            </List>
        );
    }

    return (
        <>
            {collabcore.length > 0 && (
                <>
                    <ChannelSectionHeader channel="collabcore" count={collabcore.length} />
                    <List disablePadding>
                        {collabcore.map((c, idx) =>
                            renderPersonalContactRow(c, idx === collabcore.length - 1, handlers),
                        )}
                    </List>
                </>
            )}
            {external.length > 0 && (
                <>
                    <ChannelSectionHeader channel="external" count={external.length} />
                    <List disablePadding>
                        {external.map((c, idx) =>
                            renderPersonalContactRow(c, idx === external.length - 1, handlers),
                        )}
                    </List>
                </>
            )}
        </>
    );
};

const TAB_LABELS = ['Team', 'Customers', 'Suppliers', 'Personal Network', 'Family'] as const;

// My Team sort options — literally sorts the flat mock array by whichever field
// is selected. When wired to the backend this becomes an ORDER BY on the same field.
type TeamSortField = 'department' | 'deptGroup' | 'orgHierarchy';

const TEAM_SORT_OPTIONS: { value: TeamSortField; label: string }[] = [
    { value: 'department', label: 'Department' },
    { value: 'deptGroup', label: 'Dept Group' },
    { value: 'orgHierarchy', label: 'Org Mgmt Hierarchy' },
];

const ALL_ORGANIZATIONS = 'All Organizations';

interface SpeedDialerProps {
    // Lets the host (e.g. PhoneTool) control what happens on a call attempt —
    // e.g. launch the Windows Phone Link tel: handler AND open its own Call
    // Entry Notes overlay. Falls back to a plain tel: launch when omitted
    // (standalone tab usage). phoneNumber is populated for External contacts
    // (ORG.org_pfuser_association_master.external_phone) so the host can pass
    // a real number to the tel: handler instead of opening it blank.
    onCall?: (contactName: string, phoneNumber?: string) => void;
    // Lets the host (PhoneTool) open the Voice Recording Overlay (11200) + its
    // own Call Entry Notes dialog together, mirroring onCall. Falls back to a
    // no-op when omitted (standalone tab usage has no voicemail backend wired).
    onVoicemail?: (contactName: string, pfuserUuid?: string) => void;
    // Lets the host (PhoneTool) open its own Timeclock overlay (reuses the same
    // TimeStudy dashboard the app header's "My Tasks / Timeclock" icon opens).
    // Falls back to a console warning when omitted (standalone tab usage).
    onTimeclock?: (contactName: string, pfuserUuid?: string) => void;
}

const Self: React.FC<SpeedDialerProps> = ({ onCall, onVoicemail, onTimeclock }) => {
    const [activeTab, setActiveTab] = useState(0);
    // Chat is a single global overlay (see chatSlice.ts) mounted once in
    // PlatformHeader — Speed Dial just triggers it via useChat(), it no
    // longer renders its own <ChatPanel> instance (was getting buried behind
    // other Dialogs/overlays when nested inside PhoneTool/CollabCore).
    const { openChat } = useChat();

    // MyTeam's Organization filter OPTIONS LIST mirrors the app header's
    // Organization picker (same Redux userOrganizations the auth/platform
    // header, My Tasks, and My Profile all resolve from) — but this is still
    // a separate, local `teamOrgFilter` selection, NOT the global
    // activeOrganizationUUID. Selecting a value here must never touch the
    // header's org context. See teamOrganizations below.
    const { pfUser, userOrganizations, userRoleAssignments, activeOrganizationUUID } = useAuth();

    // 'ALL' is the header's "All Organizations" sentinel string, not a real
    // GUID — never pass it straight into a uniqueidentifier SP param (crashes
    // with a type-conversion error). null means "platform-wide", which the
    // Entra presence sync endpoint already treats as "all orgs".
    const normalizedOrgUUID =
        activeOrganizationUUID && activeOrganizationUUID !== 'ALL' ? activeOrganizationUUID : null;

    // My Team roster — live data. Fetched per organization this pfuser belongs
    // to via ORG.sp_Universal_Contact_Search_Controller (apiRequestBySeq(11201, 1,
    // ...), the same endpoint Universal Contact Search already uses), then
    // batch-enriched with live presence from TEL.sp_UserPresence_Controller
    // (/api/user-presence @Manage=6). Customers/Suppliers stay on mock data
    // until the org-to-org relationship phase.
    const [teamContacts, setTeamContacts] = useState<TeamContact[]>([]);
    const [loadingTeam, setLoadingTeam] = useState(false);

    useEffect(() => {
        if (!userOrganizations.length) {
            setTeamContacts([]);
            return;
        }
        let cancelled = false;

        const loadTeam = async () => {
            setLoadingTeam(true);
            try {
                // Universal Contact Search is scoped to a single organization per
                // call (its only other caller, scr_tgc_11201, never passes an
                // aggregate "all my orgs" value) — fan out one call per org this
                // pfuser belongs to and merge, matching the per-org fetch pattern
                // already used elsewhere (scr_tgc_10393_pfuser_master.tsx).
                const perOrgResults = await Promise.all(
                    userOrganizations.map((org) =>
                        apiRequestBySeq<ContactSearchRow[]>(11201, 1, {
                            manage: 5,
                            OrganizationUuid: org.organization_uuid,
                            OnlyActive: true,
                        }).catch(() => [] as ContactSearchRow[]),
                    ),
                );
                const rows = perOrgResults.flat();

                // Speed dial is for calling OTHER people — exclude the current user.
                const others = rows.filter((r) => r.pfuser_uuid !== pfUser?.user_uuid);

                // A pfuser sharing >1 org with the caller would otherwise appear
                // once per shared org — dedupe, keeping the first occurrence.
                const seen = new Set<string>();
                const deduped = others.filter((r) => {
                    if (seen.has(r.pfuser_uuid)) return false;
                    seen.add(r.pfuser_uuid);
                    return true;
                });

                // Batch-resolve live presence for the whole roster in one call.
                const uuidList = deduped.map((r) => r.pfuser_uuid).join(',');
                const presenceByUuid = new Map<string, string>();
                if (uuidList) {
                    try {
                        const presenceResult: any = await apiRequest('/api/user-presence', 'POST', {
                            manage: 6, // GET_BULK
                            pfuser_uuid_list: uuidList,
                        });
                        (presenceResult?.data || []).forEach((p: any) => {
                            presenceByUuid.set(p.pfuser_uuid, p.presence_state);
                        });
                    } catch {
                        // Presence is best-effort — falls through to 'offline' default.
                    }
                }

                // Guarantee the organization label matches teamOrganizations' dropdown
                // values exactly (dba_name || organization_name), rather than trusting
                // whatever org name string the search SP happens to resolve.
                const orgNameByUuid = new Map(
                    userOrganizations.map((org) => [
                        org.organization_uuid,
                        org.dba_name || org.organization_name,
                    ]),
                );

                const mapped: TeamContact[] = deduped.map((r) => ({
                    pfuser_uuid: r.pfuser_uuid,
                    name: r.display_name || r.full_name,
                    department: r.role_department || 'Unassigned',
                    deptGroup: r.deptgroup || 'Unassigned',
                    orgHierarchy: r.orgmgthierarchy || 'Unassigned',
                    organization: orgNameByUuid.get(r.organization_uuid) || r.organization_name,
                    status: mapPresenceState(presenceByUuid.get(r.pfuser_uuid)),
                    // INTERIM phase-1 proxy — see ContactSearchRow.show_online_status.
                    channel: r.show_online_status ? 'collabcore' : 'external',
                    voipOptedIn: r.orgvoip_optin_status_id === 1,
                    externalPhone: r.external_phone || null,
                    externalEmail: r.preferred_email || null,
                }));

                if (!cancelled) setTeamContacts(mapped);
            } finally {
                if (!cancelled) setLoadingTeam(false);
            }
        };

        loadTeam();
        return () => {
            cancelled = true;
        };
    }, [userOrganizations, pfUser?.user_uuid]);

    // Microsoft Entra/Teams presence sync — real integration (Graph application
    // permission Presence.Read.All, admin-consented, Key Vault-backed client
    // credentials). Two independent pieces:
    //  1. Self opt-in: lets the signed-in user share THEIR OWN presence for the
    //     active org association (ORG.sp_EntraPresenceOptin_Controller, the new
    //     ORG.fk_Entra_Presence_Optin_Status lookup — mirrors the OrgVOIP/OrgChat
    //     opt-in pattern). Privacy gate: nobody's presence is polled from Graph
    //     unless they've explicitly opted in here.
    //  2. Sync Now: triggers a live Graph poll (api/entra-presence-sync) for all
    //     opted-in + active associations in the active org, then re-fetches
    //     TEL.sp_UserPresence_Controller bulk presence so the roster reflects it
    //     immediately. On-demand only for now (no background/scheduled job yet).
    //
    // My Team filter/sort — Organization dropdown + sort field over the live
    // roster below; each maps 1:1 to a future server-side WHERE / ORDER BY once
    // the search endpoint grows its own paging/sorting. Declared here (ahead of
    // the rest of the My Team filter state further down) because the presence
    // opt-in org fallback immediately below depends on it.
    const [teamOrgFilter, setTeamOrgFilter] = useState<string>(ALL_ORGANIZATIONS);

    // Which organization's association the "Share my Teams presence" toggle
    // applies to. The toggle is inherently per-association (mirrors the OrgVOIP/
    // OrgChat opt-in pattern), so it needs ONE specific org even when the
    // global header is set to "All Organizations" (activeOrganizationUUID ===
    // 'ALL') — previously this left myAssociationUuid permanently null and the
    // toggle permanently disabled. Resolution order: (1) the header's specific
    // org, if one is selected; (2) the My Team "Organization" filter above, if
    // narrowed to one org; (3) fall back to the user's first organization.
    const teamOrgFilterUuid = useMemo(() => {
        if (teamOrgFilter === ALL_ORGANIZATIONS) return null;
        const match = userOrganizations.find(
            (org) => (org.dba_name || org.organization_name) === teamOrgFilter,
        );
        return match?.organization_uuid || null;
    }, [teamOrgFilter, userOrganizations]);

    const presenceOrgUuid = useMemo(() => {
        if (activeOrganizationUUID && activeOrganizationUUID !== 'ALL')
            return activeOrganizationUUID;
        if (teamOrgFilterUuid) return teamOrgFilterUuid;
        return userOrganizations[0]?.organization_uuid || null;
    }, [activeOrganizationUUID, teamOrgFilterUuid, userOrganizations]);

    const presenceOrgName = useMemo(() => {
        const match = userOrganizations.find((org) => org.organization_uuid === presenceOrgUuid);
        return match ? match.dba_name || match.organization_name : null;
    }, [userOrganizations, presenceOrgUuid]);

    const myAssociationUuid = useMemo(
        () =>
            userRoleAssignments.find((r) => r.organization_uuid === presenceOrgUuid)
                ?.org_pfuser_association_uuid || null,
        [userRoleAssignments, presenceOrgUuid],
    );
    const [presenceOptedIn, setPresenceOptedIn] = useState(false);
    const [presenceOptinLoading, setPresenceOptinLoading] = useState(false);
    const [syncingPresence, setSyncingPresence] = useState(false);
    const [presenceSyncMessage, setPresenceSyncMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!myAssociationUuid) return;
        let cancelled = false;
        apiRequest('/api/entra-presence-optin', 'POST', {
            manage: 5,
            org_pfuser_association_uuid: myAssociationUuid,
        })
            .then((res: any) => {
                if (cancelled) return;
                const row = res?.data?.[0];
                setPresenceOptedIn(row?.entra_presence_optin_status_id === 1);
            })
            .catch(() => {
                // Best-effort — opt-in state just stays at its default (off).
            });
        return () => {
            cancelled = true;
        };
    }, [myAssociationUuid]);

    const handleTogglePresenceOptin = async (checked: boolean) => {
        if (!myAssociationUuid || !pfUser?.user_uuid) return;
        setPresenceOptinLoading(true);
        try {
            await apiRequest('/api/entra-presence-optin', 'POST', {
                manage: 1,
                pfuser_uuid: pfUser.user_uuid,
                org_pfuser_association_uuid: myAssociationUuid,
                entra_presence_optin_status_id: checked ? 1 : 2,
            });
            setPresenceOptedIn(checked);
        } catch (err) {
            console.error('Failed to update Entra presence opt-in', err);
        } finally {
            setPresenceOptinLoading(false);
        }
    };

    const handleSyncPresenceNow = async () => {
        if (!activeOrganizationUUID) return;
        setSyncingPresence(true);
        setPresenceSyncMessage(null);
        try {
            const syncResult: any = await apiRequest('/api/entra-presence-sync', 'POST', {
                manage: 1,
                organization_uuid: normalizedOrgUUID,
            });
            const syncedCount = syncResult?.data?.synced_count ?? 0;
            setPresenceSyncMessage(
                syncedCount > 0
                    ? `Synced presence for ${syncedCount} teammate${syncedCount === 1 ? '' : 's'}.`
                    : 'No opted-in teammates to sync yet.',
            );

            // Re-fetch live presence for the roster so the UI reflects it immediately.
            const uuidList = teamContacts.map((c) => c.pfuser_uuid).join(',');
            if (uuidList) {
                const presenceResult: any = await apiRequest('/api/user-presence', 'POST', {
                    manage: 6,
                    pfuser_uuid_list: uuidList,
                });
                const presenceByUuid = new Map<string, string>();
                (presenceResult?.data || []).forEach((p: any) => {
                    presenceByUuid.set(p.pfuser_uuid, p.presence_state);
                });
                setTeamContacts((prev) =>
                    prev.map((c) =>
                        presenceByUuid.has(c.pfuser_uuid)
                            ? { ...c, status: mapPresenceState(presenceByUuid.get(c.pfuser_uuid)) }
                            : c,
                    ),
                );
            }
        } catch (err) {
            console.error('Entra presence sync failed', err);
            setPresenceSyncMessage('Presence sync failed. Please try again.');
        } finally {
            setSyncingPresence(false);
        }
    };

    // Shared search box — "architected the same way" across every tab so the
    // real search algorithm can be dropped in later without changing the UI.
    const [searchQuery, setSearchQuery] = useState('');

    // Sort field over the live roster — teamOrgFilter itself is declared
    // earlier (see the presence opt-in org fallback above). Maps 1:1 to a
    // future server-side ORDER BY once the search endpoint grows its own
    // paging/sorting.
    const [teamSortField, setTeamSortField] = useState<TeamSortField>('department');

    // Customers/Suppliers co-tenancy filter — when on, only show orgs we share
    // tenancy with (i.e. can see their published contact numbers).
    const [coTenantOnly, setCoTenantOnly] = useState(false);

    // Customers/Suppliers organization filters — local to this component only.
    // Deliberately NOT wired to the auth/platform-header organization selector
    // (activeOrganizationUUID) — that dropdown drives security context and must
    // stay untouched. These are independent, per-tab contact-list filters whose
    // population differs by relationship (MyOrg<->TheirOrg per tab). Mock data
    // only for now; the real org list will come from a left join against
    // CAT.Lib_Department (dept group roles master) in a follow-up step.
    const [customerOrgFilter, setCustomerOrgFilter] = useState<string>(ALL_ORGANIZATIONS);
    const [supplierOrgFilter, setSupplierOrgFilter] = useState<string>(ALL_ORGANIZATIONS);

    // Sourced from Redux (useAuth → userOrganizations), the same org list the
    // app header/My Tasks/My Profile already resolve from — teamContacts'
    // `organization` field is mapped to match these exact labels (see
    // orgNameByUuid above) so this filter always has matching data.
    const teamOrganizations = useMemo(
        () => [
            ALL_ORGANIZATIONS,
            ...Array.from(
                new Set(userOrganizations.map((org) => org.dba_name || org.organization_name)),
            ),
        ],
        [userOrganizations],
    );

    const customerOrganizations = useMemo(
        () => [
            ALL_ORGANIZATIONS,
            ...Array.from(new Set(customerContacts.map((c) => c.orgLegalName))),
        ],
        [],
    );

    const supplierOrganizations = useMemo(
        () => [
            ALL_ORGANIZATIONS,
            ...Array.from(new Set(supplierContacts.map((c) => c.orgLegalName))),
        ],
        [],
    );

    const matchesSearch = (name: string, extra?: string) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.trim().toLowerCase();
        return name.toLowerCase().includes(q) || Boolean(extra && extra.toLowerCase().includes(q));
    };

    const filteredTeamContacts = useMemo(() => {
        const list = teamContacts
            .filter((c) => teamOrgFilter === ALL_ORGANIZATIONS || c.organization === teamOrgFilter)
            .filter((c) => matchesSearch(c.name, c.department));
        return [...list].sort((a, b) => a[teamSortField].localeCompare(b[teamSortField]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamContacts, teamOrgFilter, teamSortField, searchQuery]);

    const filteredCustomerContacts = useMemo(
        () =>
            customerContacts
                .filter(
                    (c) =>
                        customerOrgFilter === ALL_ORGANIZATIONS ||
                        c.orgLegalName === customerOrgFilter,
                )
                .filter((c) => !coTenantOnly || c.coTenant)
                .filter((c) => matchesSearch(c.name, c.orgLegalName)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customerOrgFilter, coTenantOnly, searchQuery],
    );

    const filteredSupplierContacts = useMemo(
        () =>
            supplierContacts
                .filter(
                    (c) =>
                        supplierOrgFilter === ALL_ORGANIZATIONS ||
                        c.orgLegalName === supplierOrgFilter,
                )
                .filter((c) => !coTenantOnly || c.coTenant)
                .filter((c) => matchesSearch(c.name, c.orgLegalName)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [supplierOrgFilter, coTenantOnly, searchQuery],
    );

    const filteredPersonalContacts = useMemo(
        () => personalNetworkContacts.filter((c) => matchesSearch(c.name, c.relation)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchQuery],
    );

    const filteredFamilyContacts = useMemo(
        () => familyContacts.filter((c) => matchesSearch(c.name, c.relation)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchQuery],
    );

    const handleCall = (contactName: string, phoneNumber?: string) => {
        if (onCall) {
            onCall(contactName, phoneNumber);
        } else {
            window.location.href = phoneNumber ? `tel:${phoneNumber}` : 'tel:';
        }
    };

    const handleChat = (_contactName: string) => {
        openChat();
    };

    const handleVoicemail = (contactName: string, pfuserUuid?: string) => {
        if (onVoicemail) {
            onVoicemail(contactName, pfuserUuid);
        } else {
            console.warn(
                `[Speed Dialer] Voicemail requested for ${contactName}, but no onVoicemail host handler is wired (standalone tab usage).`,
            );
        }
    };

    const handleTimeclock = (contactName: string, pfuserUuid?: string) => {
        if (onTimeclock) {
            onTimeclock(contactName, pfuserUuid);
        } else {
            console.warn(
                `[Speed Dialer] Timeclock requested for ${contactName}, but no onTimeclock host handler is wired (standalone tab usage).`,
            );
        }
    };

    const actionHandlers: ContactActionHandlers = {
        onCall: handleCall,
        onChat: handleChat,
        onTeamsCall: launchTeamsCall,
        onVoicemail: handleVoicemail,
        onMeeting: launchTeamsMeeting,
        onTimeclock: handleTimeclock,
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Paper
                variant="outlined"
                sx={{ display: 'flex', flexDirection: 'column', minHeight: 480 }}
            >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search contacts by name, department, or organization…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <Tabs
                        orientation="vertical"
                        variant="scrollable"
                        value={activeTab}
                        onChange={(_e, value) => setActiveTab(value)}
                        sx={{
                            borderRight: 1,
                            borderColor: 'divider',
                            minWidth: 180,
                            '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left' },
                        }}
                    >
                        {TAB_LABELS.map((label) => (
                            <Tab key={label} label={label} />
                        ))}
                    </Tabs>
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        <ContactChannelLegend />
                        {activeTab === 0 && (
                            <>
                                <Accordion
                                    defaultExpanded
                                    disableGutters
                                    sx={{ '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            <FilterListIcon fontSize="small" />
                                            <Typography variant="subtitle2">
                                                Filter &amp; Sort My Team
                                            </Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                                <InputLabel id="team-org-filter-label">
                                                    Organization
                                                </InputLabel>
                                                <Select
                                                    labelId="team-org-filter-label"
                                                    label="Organization"
                                                    value={teamOrgFilter}
                                                    onChange={(e) =>
                                                        setTeamOrgFilter(e.target.value)
                                                    }
                                                >
                                                    {teamOrganizations.map((org) => (
                                                        <MenuItem key={org} value={org}>
                                                            {org}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                                <InputLabel id="team-sort-label">
                                                    Sort By
                                                </InputLabel>
                                                <Select
                                                    labelId="team-sort-label"
                                                    label="Sort By"
                                                    value={teamSortField}
                                                    onChange={(e) =>
                                                        setTeamSortField(
                                                            e.target.value as TeamSortField,
                                                        )
                                                    }
                                                >
                                                    {TEAM_SORT_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            <SyncIcon fontSize="small" />
                                            <Typography variant="subtitle2">
                                                Microsoft Teams Presence Sync
                                            </Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={2}
                                            sx={{ alignItems: { sm: 'center' } }}
                                        >
                                            <Tooltip title="Share your real Microsoft Teams availability with teammates in Speed Dial. Off by default — no presence is synced until you opt in.">
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={presenceOptedIn}
                                                            disabled={
                                                                !myAssociationUuid ||
                                                                presenceOptinLoading
                                                            }
                                                            onChange={(e) =>
                                                                handleTogglePresenceOptin(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Share my Teams presence"
                                                />
                                            </Tooltip>
                                            {presenceOrgName && userOrganizations.length > 1 && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Applies to: {presenceOrgName}
                                                </Typography>
                                            )}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={
                                                    syncingPresence ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        <SyncIcon fontSize="small" />
                                                    )
                                                }
                                                disabled={
                                                    syncingPresence || !activeOrganizationUUID
                                                }
                                                onClick={handleSyncPresenceNow}
                                            >
                                                Sync Now
                                            </Button>
                                            {presenceSyncMessage && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {presenceSyncMessage}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                                {loadingTeam ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <TeamList contacts={filteredTeamContacts} {...actionHandlers} />
                                )}
                            </>
                        )}
                        {(activeTab === 1 || activeTab === 2) && (
                            <Box sx={{ px: 2, pt: 1.5 }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    sx={{ alignItems: { sm: 'center' }, mb: 1 }}
                                >
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel id="org-relationship-filter-label">
                                            Organization
                                        </InputLabel>
                                        <Select
                                            labelId="org-relationship-filter-label"
                                            label="Organization"
                                            value={
                                                activeTab === 1
                                                    ? customerOrgFilter
                                                    : supplierOrgFilter
                                            }
                                            onChange={(e) =>
                                                activeTab === 1
                                                    ? setCustomerOrgFilter(e.target.value)
                                                    : setSupplierOrgFilter(e.target.value)
                                            }
                                        >
                                            {(activeTab === 1
                                                ? customerOrganizations
                                                : supplierOrganizations
                                            ).map((org) => (
                                                <MenuItem key={org} value={org}>
                                                    {org}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={coTenantOnly}
                                                onChange={(e) => setCoTenantOnly(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                Show only Shared-Tenancy contacts (published
                                                numbers)
                                            </Typography>
                                        }
                                    />
                                </Stack>
                            </Box>
                        )}
                        {activeTab === 1 && (
                            <OrgList contacts={filteredCustomerContacts} {...actionHandlers} />
                        )}
                        {activeTab === 2 && (
                            <OrgList contacts={filteredSupplierContacts} {...actionHandlers} />
                        )}
                        {activeTab === 3 && (
                            <PersonalList contacts={filteredPersonalContacts} {...actionHandlers} />
                        )}
                        {activeTab === 4 && (
                            <PersonalList contacts={filteredFamilyContacts} {...actionHandlers} />
                        )}
                    </Box>
                </Box>
            </Paper>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 1,
                }}
            >
                Availability: <span style={{ color: '#9e9e9e' }}>● Not online</span>{' '}
                <span style={{ color: '#4caf50' }}>● Available</span>{' '}
                <span style={{ color: '#ffc107' }}>● On call (unscheduled)</span>{' '}
                <span style={{ color: '#f44336' }}>● Busy in meeting</span>
            </Typography>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
